import * as React from "react";

import { useAuth, useNavigation } from "@/providers";
import { RobotoLinkHoverUnderline } from "@/shared/components";
import { CreateEntityButton } from "@/shared/components/CreateEntityButton";
import {
  DESCRIPTION_COLUMN_SIZING,
  ResourceView,
  filterToCondition,
  qualifyPrincipal,
  QuickSearch,
  QuickSearchButton,
  renderDescription,
  renderPrincipal,
  useQuickSearchHotkey,
  useResourceView,
  type FilterDefinition,
  type QuickSearchProperty,
} from "@/shared/components/ResourceView";
import {
  ROW_ACTIONS_COLUMN_ID,
  RowActionsMenu,
  rowActionsColumn,
  type RowAction,
} from "@/shared/components/RowActionsMenu";
import { type ActionRecord } from "@/shared/domain/actions";
import { type ActionQueryScope } from "@/shared/domain/actions/ActionService";
import {
  useActions,
  useActionsPage,
  useActionTags,
  useDeleteAction,
} from "@/shared/domain/actions/hooks";
import {
  ACTION_VERB_ICONS,
  Trash2,
  Zap as ZapIcon,
} from "@/shared/foundation/icons";
import { Banner, ConfirmDialog } from "@/shared/foundation/primitives";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  cellStyles,
  colId,
  createColumnHelper,
  deriveLoadingState,
  renderDashOr,
  renderDashOrValue,
  useCellDeps,
  type CellDeps,
  type ColDef,
  type ColumnPickerOption,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import type { SearchQueryBody, SortDirection } from "@/types";

import { actionDetailPath, ownerOrgIdIfNotViewers } from "../../model";
import InvokeActionButton from "../action/InvokeActionButton";

import styles from "./ActionsList.module.css";
import { publisherName } from "./publisherName";

/** The repository an action's image comes from: the last segment of its URI. */
function imageRepository(action: ActionRecord): string | undefined {
  const parts = action.uri?.split("/") ?? [];
  return parts.length > 0 ? parts[parts.length - 1] : undefined;
}

const columnHelper = createColumnHelper<ActionRecord>();

const RunIcon = ACTION_VERB_ICONS.run;

interface Callbacks {
  viewerOrgId: string | undefined;
  rowActions: (action: ActionRecord) => RowAction[];
}

/**
 * Every column either list can show, keyed by id. Cells read `deps` so the
 * column array keeps one identity for the table's lifetime (see
 * `useCellDeps`). Only the Name cell navigates; `DataTable` has no row click.
 *
 * A sortable header sorts on the server, so a column is sortable only when
 * the query endpoint orders by it. The image comes from the action's current
 * version, which the endpoint cannot order by, so that column is not.
 */
function buildColumns(
  deps: CellDeps<Callbacks>,
): Record<string, ColDef<ActionRecord>> {
  return {
    name: columnHelper.accessor("name", {
      headerName: "Name",
      flex: 2,
      minWidth: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<ActionRecord, string>) => {
        const action = p.data;
        if (!action) {
          return null;
        }
        return (
          <RobotoLinkHoverUnderline
            to={actionDetailPath(action, deps.current.viewerOrgId)}
            className={cellStyles.primaryContent}
          >
            {action.name}
          </RobotoLinkHoverUnderline>
        );
      },
    }),
    publisher: columnHelper.accessor((action) => publisherName(action.org_id), {
      colId: "publisher",
      headerName: "Publisher",
      minWidth: 140,
      sortable: true,
      cellRenderer: renderDashOrValue,
    }),
    description: columnHelper.accessor(
      (action) => action.short_description ?? "",
      {
        colId: "description",
        headerName: "Description",
        ...DESCRIPTION_COLUMN_SIZING,
        cellRenderer: renderDescription,
      },
    ),
    image: columnHelper.accessor((action) => imageRepository(action) ?? "", {
      colId: "image",
      headerName: "Image",
      minWidth: 140,
      sortable: false,
      cellClass: cellStyles.monoCell,
      cellRenderer: renderDashOr(
        (p: ICellRendererParams<ActionRecord, string>) => (
          <span className={styles.ellipsis} title={p.data?.uri}>
            {p.value}
          </span>
        ),
      ),
    }),
    tags: columnHelper.accessor("tags", {
      headerName: "Tags",
      flex: 2,
      minWidth: 180,
      cellClass: cellStyles.tagsCell,
      valueFormatter: (p) => (p.value ?? []).join(", "),
      cellRenderer: renderDashOr(
        (p: ICellRendererParams<ActionRecord, string[]>) => (
          <TagGroup tags={p.value ?? []} maxDisplayCount={2} />
        ),
      ),
    }),
    created: columnHelper.timestamp("created", {
      headerName: "Created",
      minWidth: 120,
      sortable: true,
      variant: "relative",
    }),
    created_by: columnHelper.accessor(
      (action) => qualifyPrincipal(action.created_by),
      {
        colId: "created_by",
        headerName: "Created by",
        minWidth: 150,
        sortable: true,
        cellRenderer: renderPrincipal,
      },
    ),
    modified: columnHelper.timestamp("modified", {
      headerName: "Modified",
      minWidth: 120,
      sortable: true,
      variant: "relative",
    }),
    modified_by: columnHelper.accessor(
      (action) => qualifyPrincipal(action.modified_by),
      {
        colId: "modified_by",
        headerName: "Modified by",
        minWidth: 150,
        sortable: true,
        cellRenderer: renderPrincipal,
      },
    ),
    [ROW_ACTIONS_COLUMN_ID]: rowActionsColumn<ActionRecord>((action) => (
      <RowActionsMenu
        label={action.name}
        actions={deps.current.rowActions(action)}
      />
    )),
  };
}

/**
 * Per scope: the columns in display order, and the ones shown on a first
 * visit. The rest are offered by the column picker, unchecked.
 */
const LAYOUT: Record<
  ActionQueryScope,
  { order: string[]; defaultVisible: string[] }
> = {
  org: {
    order: [
      "name",
      "description",
      "image",
      "tags",
      "created",
      "created_by",
      "modified",
      "modified_by",
    ],
    defaultVisible: [
      "name",
      "description",
      "image",
      "tags",
      "created_by",
      "modified",
    ],
  },
  actionhub: {
    order: [
      "name",
      "publisher",
      "description",
      "tags",
      "image",
      "created",
      "modified",
    ],
    defaultVisible: ["name", "publisher", "description", "tags", "modified"],
  },
};

/**
 * The record field the server orders by for a column whose id is not one.
 * Publisher is the owning org, shown by name; the server orders by its id.
 */
const SERVER_SORT_FIELDS: Record<string, string> = {
  publisher: "org_id",
};

// Actions have no id, so quick find is a shortcut to a name search.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "name", name: "Name", fallback: true },
];

const EMPTY_COPY: Record<ActionQueryScope, string> = {
  org:
    "Actions run code on datasets uploaded to Roboto. Use them to process " +
    "data, create events or generate artifacts. Actions here are private to " +
    "your organization.",
  actionhub:
    "Actions in the Action Hub are published by Roboto and other " +
    "organizations, and you can invoke them on your own datasets.",
};

interface ActionsListProps {
  /** `org` lists the current org's actions; `actionhub` the Action Hub's. */
  scope: ActionQueryScope;
}

/**
 * The Actions and Action Hub tabs: the actions in `scope` in a `DataTable`
 * with the shared list toolbar (Filter, Columns, then the page's buttons).
 *
 * The table's filters, sort and page go to the query endpoint as one
 * specification, and the rows shown are the page the server answered with.
 * Nothing is filtered, sorted or paged here. The Filter menu's tag options
 * come from the tags endpoints; the hub's Publisher options from the hub.
 */
export function ActionsList({ scope }: ActionsListProps) {
  const { currentOrganization } = useAuth();
  const { goto } = useNavigation();
  const viewerOrgId = currentOrganization?.org_id;
  const deleteAction = useDeleteAction();
  const [pendingDelete, setPendingDelete] = React.useState<ActionRecord | null>(
    null,
  );
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const rowActions = React.useCallback(
    (action: ActionRecord): RowAction[] => {
      const invoke: RowAction = {
        label: action.uri ? "Invoke" : "Invoke (no image)",
        icon: <RunIcon aria-hidden data-action-verb="run" />,
        disabled: !action.uri,
        onClick: () =>
          goto.invokeAction({
            action: {
              name: action.name,
              owner: ownerOrgIdIfNotViewers(action, viewerOrgId),
            },
          }),
      };
      // Only an action's own org can delete it, and only from its own list.
      if (scope !== "org" || action.org_id !== viewerOrgId) {
        return [invoke];
      }
      return [
        invoke,
        {
          label: "Delete",
          icon: <Trash2 />,
          danger: true,
          onClick: () => setPendingDelete(action),
        },
      ];
    },
    [goto, scope, viewerOrgId],
  );

  const cellDeps = useCellDeps<Callbacks>({ viewerOrgId, rowActions });

  const { order, defaultVisible } = LAYOUT[scope];
  const [columns] = React.useState<ColDef<ActionRecord>[]>(() => {
    const all = buildColumns(cellDeps);
    return [...order, ROW_ACTIONS_COLUMN_ID].map((id) => all[id]);
  });
  const pickerOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns
        .filter((column) => colId(column) !== ROW_ACTIONS_COLUMN_ID)
        .map((column) => ({
          id: colId(column),
          label: column.headerName ?? colId(column),
        })),
    [columns],
  );

  const view = useResourceView<ActionRecord>({
    getRowId: (action) => `${action.org_id}/${action.name}`,
    initialSort: { columnId: "modified", direction: "desc" },
    defaultVisibleColumnIds: defaultVisible,
  });

  const quickSearch = useQuickSearchHotkey();

  // The Filter menu's vocabulary. The hub's publishers are its distinct
  // owning orgs; no endpoint lists them, and the hub is a few dozen actions
  // that the invoke pickers read whole anyway, so that read is shared here.
  const tagsQuery = useActionTags(scope);
  const hubQuery = useActions("actionhub", undefined, {
    enabled: scope === "actionhub",
  });

  const filterDefinitions = React.useMemo<FilterDefinition[]>(() => {
    const definitions: FilterDefinition[] = [
      {
        type: "string",
        field: "name",
        name: "Name",
        allowedComparators: ["CONTAINS", "EQUALS"],
      },
      {
        type: "string",
        field: "short_description",
        name: "Description",
        allowedComparators: ["CONTAINS"],
      },
      {
        type: "set",
        field: "tags",
        name: "Tags",
        options: tagsQuery.data ?? [],
      },
    ];
    if (scope === "actionhub") {
      const publishers = new Set(
        (hubQuery.data ?? []).map((action) => action.org_id),
      );
      definitions.push({
        type: "enum",
        field: "org_id",
        name: "Publisher",
        options: [...publishers].sort().map((orgId) => ({
          label: publisherName(orgId),
          value: orgId,
        })),
      });
    } else {
      definitions.push({
        type: "string",
        field: "created_by",
        name: "Created by",
        allowedComparators: ["CONTAINS", "EQUALS"],
      });
    }
    return definitions;
  }, [tagsQuery.data, hubQuery.data, scope]);

  const condition = React.useMemo(
    () =>
      view.filters.type === "structured" && view.filters.filters.length > 0
        ? filterToCondition(view.filters.filters, {
            matchMode: view.filters.matchMode,
            definitions: filterDefinitions,
          })
        : undefined,
    [view.filters, filterDefinitions],
  );

  // Token chain for forward paging: the entry at key N is the `after` token
  // to send for page N, which page N-1's response handed out. Page 0 sends
  // none. The chain empties whenever the query's shape changes, which is
  // when `useResourceView` returns the table to page 0 as well.
  const [tokenChain, setTokenChain] = React.useState<Map<number, string>>(
    () => new Map(),
  );
  React.useEffect(() => {
    setTokenChain(new Map());
  }, [view.filters, view.table.sort, view.table.pageSize]);

  const sort = view.table.sort;
  const currentPage = view.table.currentPage;
  const after = currentPage > 0 ? tokenChain.get(currentPage) : undefined;
  const query: SearchQueryBody = {
    ...(sort && {
      sort_by: SERVER_SORT_FIELDS[sort.columnId] ?? sort.columnId,
      sort_direction: sort.direction.toUpperCase() as SortDirection,
    }),
    ...(condition && { condition }),
    ...(after && { after }),
    limit: view.table.pageSize,
  };
  const actionsQuery = useActionsPage(scope, query);

  // Store the next page's token from each settled response. Keyed on the
  // response itself, not the token's text: tokens are offsets, so a new sort
  // or filter on page 0 answers with the same "50" the old one did, and an
  // effect watching the text would never refill the chain the reset above
  // just emptied. The placeholder a refetch shows is the old response, so it
  // is skipped rather than stored under the new query. Declared after the
  // reset so a cached answer arriving in the same render is stored, not wiped.
  const settled = actionsQuery.isPlaceholderData
    ? undefined
    : actionsQuery.data;
  React.useEffect(() => {
    const token = settled?.next_token;
    if (!token) {
      return;
    }
    setTokenChain((prev) => {
      if (prev.get(currentPage + 1) === token) {
        return prev;
      }
      const next = new Map(prev);
      next.set(currentPage + 1, token);
      return next;
    });
  }, [settled, currentPage]);
  const nextToken = actionsQuery.data?.next_token ?? null;

  const rows = actionsQuery.data?.items ?? [];
  const loading = deriveLoadingState({
    isLoading: actionsQuery.isLoading,
    isFetching: actionsQuery.isFetching,
  });

  const confirmDelete = async () => {
    if (pendingDelete === null || viewerOrgId === undefined) {
      return;
    }
    setDeleteError(null);
    try {
      await deleteAction.mutateAsync({
        name: pendingDelete.name,
        resourceOwnerId: viewerOrgId,
      });
      setPendingDelete(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      ErrorMonitoringService.captureError(error, { action: pendingDelete });
      setDeleteError(`Couldn't delete ${pendingDelete.name}. ${error.message}`);
      setPendingDelete(null);
    }
  };

  const createButton = (variant: "primary" | "secondary") => (
    <CreateEntityButton
      entity="action"
      variant={variant}
      onClick={() => goto.createAction()}
    />
  );

  return (
    <div className={styles.page}>
      {actionsQuery.isError && (
        <Banner variant="error" className={styles.banner}>
          Failed to load actions. {actionsQuery.error?.message ?? ""}
        </Banner>
      )}
      {deleteError !== null && (
        <Banner variant="error" className={styles.banner}>
          {deleteError}
        </Banner>
      )}
      <QuickSearch
        open={quickSearch.isOpen}
        onClose={quickSearch.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder="Search by name…"
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <QuickSearchButton onClick={quickSearch.open} label="Find actions" />
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={pickerOptions}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {scope === "org" && (
            <div className={styles.actionsRight}>
              <InvokeActionButton />
              {createButton("primary")}
            </div>
          )}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<ActionRecord>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={nextToken !== null}
          loading={loading}
          columns={columns}
          data={rows}
          ariaLabel={scope === "org" ? "Actions" : "Action Hub"}
          noRowsContent={
            <ResourceView.EmptyState
              hasActiveFilters={view.hasActiveFilters}
              noData={{
                icon: ZapIcon,
                title:
                  scope === "org"
                    ? "No actions yet"
                    : "No actions in the Action Hub",
                description: EMPTY_COPY[scope],
                action: scope === "org" ? createButton("secondary") : undefined,
              }}
              noMatches={{
                icon: ZapIcon,
                title: "No matching actions",
                description: "Try adjusting or clearing your filters.",
              }}
            />
          }
        />
      </ResourceView.Root>
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteAction.isPending) {
            setPendingDelete(null);
          }
        }}
        title={`Delete ${pendingDelete?.name ?? "action"}?`}
        description="This action cannot be undone."
        confirmLabel="Delete"
        destructive
        pending={deleteAction.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
